import { Table as TableCore, Box } from '@octano/global-ui';
import { Col, Row } from 'reactstrap';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Table as TableCollapsable } from '../../../components/table-collapsable';
import { STUDENT_STATUS } from '../../../types/studentStatusTypes';
import {
  DegreeProcessActivity,
  DegreeProcessRendition,
  DegreeProcessRenditionRow,
} from '../types';
import { useColumns } from './useColumns';
import { useColumnsRenditions } from './useColumnsRenditions';
import useUserState from '../../../hooks/useUserState';
import { PermissionName } from '../../../types/Auth';

export interface TableProps {
  data: DegreeProcessActivity[];
  gradeAverage: number;
  toggleModalRendition: (
    activityId: number | string,
    editRendition?: DegreeProcessRendition,
  ) => any;
  enrollmentStatus: string;
  toggleDeleteModal: (editRendition?: DegreeProcessRendition) => void;
}

export default function Table({
  data,
  gradeAverage,
  toggleModalRendition,
  enrollmentStatus,
  toggleDeleteModal,
}: TableProps) {
  const { t } = useTranslation();
  const columns = useColumns();
  const { isAuthorizedTo } = useUserState();

  const hasPermission = useMemo(
    () =>
      isAuthorizedTo(
        [PermissionName.STUDENT_MANAGEMENT_DEGREE_PROCESS_STUDENT_MANAGE],
        true,
      ),
    [isAuthorizedTo],
  );

  const columnsRenditions = useColumnsRenditions({
    toggleModalRendition,
    canEdit: enrollmentStatus === STUDENT_STATUS.GRADUATE && hasPermission,
    toggleDeleteModal,
  });

  const dataBuildEmptyRow = useCallback(
    (id: number | string, renditions?: DegreeProcessRendition[]) => ({
      buildEmptyRow: true,
      activityId: id,
      hasApprovedRenditions: renditions?.some(
        (rendition) => rendition.approved,
      ),
    }),
    [],
  );

  return (
    <>
      <TableCollapsable<DegreeProcessActivity>
        columns={columns}
        secondColumn={{
          tdClassName: 'px-4',
          columnName: 'quota',
          headerText: t(`table.quota`),
          cellFormat: ({ row }) => {
            const buildEmptyRow =
              enrollmentStatus === STUDENT_STATUS.GRADUATE
                ? dataBuildEmptyRow(row.id, row.renditions)
                : dataBuildEmptyRow(0);
            const currentRedition = row?.renditions[0];
            return (
              <TableCore<DegreeProcessRenditionRow>
                borderless={false}
                color="transparent"
                striped
                columns={columnsRenditions(currentRedition?.id)}
                data={
                  enrollmentStatus === STUDENT_STATUS.GRADUATE
                    ? [...row.renditions, buildEmptyRow]
                    : row.renditions
                }
              />
            );
          },
        }}
        data={data}
      />
      <Box
        body={
          <Row className="justify-content-end pr-4">
            <Col xs="auto" className="font-weight-bold">
              {t('studentsDegreeStudent.tableActivities.gradeAverage')}
              <span> {gradeAverage > 0 ? gradeAverage : 'N/A'}</span>
            </Col>
          </Row>
        }
        color="secondary"
        title=""
        variant="standard"
        fullwidth
      />
    </>
  );
}
